import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Share, X, Check } from "lucide-react";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export const InstallHelp = ({ onClose }) => createPortal(
<div className="fixed inset-0 z-[3000] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="install-help-modal" onClick={onClose}>
          <div className="w-full max-w-sm rl-card p-5 rl-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-black text-lg text-slate-900">Install ROYAL LAKSHMI</h3>
              <button onClick={onClose} data-testid="install-help-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {isIOS() ? (
              <div className="text-sm text-slate-600 space-y-3">
                <p>To install on iPhone/iPad:</p>
                <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">1</span> Tap the <Share className="w-4 h-4 inline text-blue-600" /> Share button in Safari</div>
                <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">2</span> Scroll and tap <b>"Add to Home Screen"</b></div>
                <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">3</span> Tap <b>"Add"</b> — done!</div>
              </div>
            ) : (
              <div className="text-sm text-slate-600 space-y-3">
                <p>To install on Android:</p>
                <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">1</span> Open your browser menu (⋮ top-right)</div>
                <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">2</span> Tap <b>"Install app"</b> or <b>"Add to Home screen"</b></div>
                <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">3</span> Confirm — the app icon appears on your home screen!</div>
              </div>
            )}
            <button onClick={onClose} className="mt-4 w-full rl-gold-bg font-bold py-2.5 rounded-xl text-sm" data-testid="install-help-ok">Got it</button>
          </div>
        </div>
, document.body);

export const InstallAppButton = () => {
  const [prompt, setPrompt] = useState(typeof window !== "undefined" ? window.__deferredInstallPrompt : null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const onAvail = () => setPrompt(window.__deferredInstallPrompt);
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onAvail);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (prompt) {
      prompt.prompt();
      const res = await prompt.userChoice;
      if (res && res.outcome === "accepted") setInstalled(true);
      window.__deferredInstallPrompt = null;
      setPrompt(null);
    } else {
      setShowHelp(true);
    }
  };

  if (installed) {
    return (
      <div className="rl-card p-4 flex items-center gap-3" data-testid="install-app-installed">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Check className="w-5 h-5 text-emerald-600" /></div>
        <div>
          <div className="font-semibold text-slate-900 text-sm">App Installed</div>
          <div className="text-[11px] text-slate-500">ROYAL LAKSHMI is on your home screen</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button onClick={handleClick} className="w-full rl-card p-4 flex items-center gap-3 text-left" data-testid="install-app-btn">
        <div className="w-10 h-10 rounded-xl rl-gold-bg flex items-center justify-center"><Download className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="font-semibold text-slate-900 text-sm">Install App</div>
          <div className="text-[11px] text-slate-500">Add ROYAL LAKSHMI to your home screen for quick access</div>
        </div>
      </button>

      {showHelp && <InstallHelp onClose={() => setShowHelp(false)} />}
    </>
  );
};
